import { isDevPreview } from '../boot/devPreview';
import { sb } from './supabase';
import { logActivity } from './repo';
import { parseDocument, type CadDocument } from '../cad/model/document';
import type { CadOp, OpBatch } from '../cad/ops/operation';
import type { DrawingVersion, VersionStatus } from './types';

/**
 * 버전·브랜치 저장소 (작업지시서 P4-08 / P4-10)
 *
 * ── 이 파일이 지키는 것 ─────────────────────────────────────────────────
 *
 *   **과거 버전을 복원해도 이력이 사라지지 않는다.**
 *
 *   흔한 구현은 "복원 = 과거 스냅샷을 현재 버전에 덮어쓰기" 인데,
 *   그러면 되돌리기 전 상태가 영원히 사라진다. 리뉴얼 검토 중
 *   "A안으로 되돌렸다가 역시 B안이 낫겠다" 는 매번 일어나는 일이다.
 *
 *   → 복원은 **새 버전을 만든다.** 과거 스냅샷을 본문으로 갖는 새 버전이
 *     최신으로 붙고, 그 사이 버전들은 그대로 남는다.
 *
 *   승인·배포된 버전은 편집하지 않는다. 그 위에서 작업하려면 분기한다.
 */

export interface BranchRow {
  id: string;
  drawing_id: string;
  name: string;
  parent_version_id: string | null;
  is_main: boolean;
  created_by: string;
  created_at: string;
}

/** 버전 목록 한 줄 — 본문(document_json)은 빼고 가져온다 */
export type VersionListItem = DrawingVersion & {
  parent_version_id: string | null;
  tags: string[];
  branch_name?: string;
};

function fail(where: string, error: { message: string } | null): void {
  if (error) throw new Error(`${where}: ${error.message}`);
}

/* -------------------------------------------------------------------------- *
 * 조회
 * -------------------------------------------------------------------------- */

/**
 * 도면의 버전 목록.
 *
 * ⚠️ `document_json` 을 절대 select 하지 않는다. 도면 하나에 수 MB 인데
 *    목록 화면이 전부 받아오면 브라우저가 멈춘다.
 */
export async function listVersions(drawingId: string): Promise<VersionListItem[]> {
  if (isDevPreview()) return devVersions(drawingId);

  const client = sb();
  const { data, error } = await client
    .from('cad_version')
    .select(
      'id, drawing_id, branch_id, parent_version_id, version_number, name, change_summary, tags, status, entity_count, created_by, created_at'
    )
    .eq('drawing_id', drawingId)
    .order('created_at', { ascending: false });
  fail('버전 목록 조회', error);

  return (data ?? []) as VersionListItem[];
}

export async function listBranches(drawingId: string): Promise<BranchRow[]> {
  if (isDevPreview()) return devBranches(drawingId);

  const client = sb();
  const { data, error } = await client
    .from('cad_branch')
    .select('*')
    .eq('drawing_id', drawingId)
    .order('is_main', { ascending: false })
    .order('created_at', { ascending: true });
  fail('브랜치 목록 조회', error);

  return (data ?? []) as BranchRow[];
}

/** 버전 본문만 가져온다 (비교 화면이 두 번 호출한다) */
export async function loadVersionDocument(versionId: string): Promise<CadDocument> {
  if (isDevPreview()) return devDocument(versionId);

  const client = sb();
  const { data, error } = await client
    .from('cad_version')
    .select('document_json, doc_path')
    .eq('id', versionId)
    .maybeSingle();
  fail('버전 본문 조회', error);
  if (!data) throw new Error('버전을 찾을 수 없거나 접근 권한이 없습니다.');

  const row = data as { document_json: unknown; doc_path: string | null };
  if (row.document_json) return parseDocument(row.document_json);

  if (row.doc_path) {
    const { data: file, error: e2 } = await client.storage
      .from('cad-derived')
      .download(row.doc_path);
    fail('도면 본문 다운로드', e2);
    return parseDocument(JSON.parse(await file!.text()));
  }

  throw new Error('버전에 본문이 없습니다.');
}

/* -------------------------------------------------------------------------- *
 * 생성
 * -------------------------------------------------------------------------- */

export interface CreateVersionInput {
  drawingId: string;
  branchId: string;
  /** 본문. 지정하지 않으면 parentVersionId 의 본문을 복사한다. */
  document?: CadDocument;
  parentVersionId?: string | null;
  name?: string;
  changeSummary?: string | null;
}

/**
 * 같은 브랜치에 새 버전을 만든다.
 *
 * version_number 는 **브랜치 안에서** 이어진다. 도면 전체로 매기면
 * 분기한 순간 번호가 뒤엉켜 "v3 다음이 v7" 같은 목록이 나온다.
 */
export async function createVersion(input: CreateVersionInput): Promise<DrawingVersion> {
  const doc = input.document ?? (await loadVersionDocument(input.parentVersionId!));

  if (isDevPreview()) {
    return devNewVersion(input, doc);
  }

  const client = sb();

  const { data: last, error: eMax } = await client
    .from('cad_version')
    .select('version_number')
    .eq('branch_id', input.branchId)
    .order('version_number', { ascending: false })
    .limit(1)
    .maybeSingle();
  fail('버전 번호 조회', eMax);

  const next = ((last as { version_number: number } | null)?.version_number ?? 0) + 1;

  const { data, error } = await client
    .from('cad_version')
    .insert({
      drawing_id: input.drawingId,
      branch_id: input.branchId,
      parent_version_id: input.parentVersionId ?? null,
      version_number: next,
      name: input.name ?? `v${next}`,
      change_summary: input.changeSummary ?? null,
      status: 'draft',
      document_json: doc,
      entity_count: doc.entities.length,
    })
    .select()
    .single();
  fail('버전 생성', error);

  await logActivity('version.create', {
    drawing: input.drawingId,
    version: (data as DrawingVersion).id,
  });

  return data as DrawingVersion;
}

/* -------------------------------------------------------------------------- *
 * 분기
 * -------------------------------------------------------------------------- */

/**
 * 특정 버전에서 새 브랜치를 딴다 (= A안 / B안).
 *
 * 승인본 위에서 계속 그리고 싶을 때 쓰는 정상 경로다.
 * 브랜치를 만들면 그 버전의 본문을 복사한 v1 이 함께 생긴다 —
 * 빈 브랜치는 사용자가 뭘 해야 할지 알 수 없다.
 */
export async function createBranch(
  drawingId: string,
  fromVersionId: string,
  name: string
): Promise<{ branch: BranchRow; version: DrawingVersion }> {
  const trimmed = name.trim();
  if (trimmed === '') throw new Error('브랜치 이름을 입력하세요.');
  if (trimmed.length > 100) throw new Error('브랜치 이름은 100자를 넘을 수 없습니다.');

  const doc = await loadVersionDocument(fromVersionId);

  if (isDevPreview()) {
    const branch = devNewBranch(drawingId, fromVersionId, trimmed);
    const version = devNewVersion(
      { drawingId, branchId: branch.id, parentVersionId: fromVersionId, name: 'v1' },
      doc
    );
    return { branch, version };
  }

  const client = sb();

  const { data: b, error: eB } = await client
    .from('cad_branch')
    .insert({
      drawing_id: drawingId,
      name: trimmed,
      parent_version_id: fromVersionId,
      is_main: false,
    })
    .select()
    .single();
  // unique (drawing_id, name) 위반은 사용자가 고칠 수 있는 실수다
  if (eB?.message.includes('duplicate key')) {
    throw new Error(`같은 이름의 안이 이미 있습니다: ${trimmed}`);
  }
  fail('브랜치 생성', eB);

  const branch = b as BranchRow;

  const version = await createVersion({
    drawingId,
    branchId: branch.id,
    document: doc,
    parentVersionId: fromVersionId,
    name: 'v1',
    changeSummary: `${trimmed} 분기 생성`,
  });

  await logActivity('branch.create', {
    drawing: drawingId,
    version: version.id,
    payload: { branch: branch.id, name: trimmed },
  });

  return { branch, version };
}

/* -------------------------------------------------------------------------- *
 * 복원
 * -------------------------------------------------------------------------- */

/**
 * 과거 버전으로 되돌린다.
 *
 * ★ 덮어쓰지 않는다. 과거 본문을 담은 **새 버전**을 최신으로 붙인다.
 *   그래야 "되돌렸다가 역시 원래대로" 가 가능하다. 실무에서 매번 일어난다.
 */
export async function restoreVersion(
  drawingId: string,
  branchId: string,
  sourceVersionId: string,
  sourceLabel: string
): Promise<DrawingVersion> {
  const doc = await loadVersionDocument(sourceVersionId);

  const version = await createVersion({
    drawingId,
    branchId,
    document: doc,
    parentVersionId: sourceVersionId,
    changeSummary: `${sourceLabel} 로 되돌림`,
  });

  await logActivity('version.restore', {
    drawing: drawingId,
    version: version.id,
    payload: { from: sourceVersionId, label: sourceLabel },
  });

  return version;
}

/* -------------------------------------------------------------------------- *
 * 상태 전이
 * -------------------------------------------------------------------------- */

/** 상태 전이 규칙 (P4-12). 화면과 DB 트리거가 같은 표를 본다. */
export const ALLOWED_TRANSITIONS: Record<VersionStatus, VersionStatus[]> = {
  draft: ['in_review', 'archived'],
  in_review: ['approved', 'changes_requested', 'draft'],
  changes_requested: ['in_review', 'draft', 'archived'],
  approved: ['published', 'archived', 'in_review'],
  published: ['archived'],
  archived: [],
};

export function canTransition(from: VersionStatus, to: VersionStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export async function setVersionStatus(
  versionId: string,
  from: VersionStatus,
  to: VersionStatus
): Promise<void> {
  if (!canTransition(from, to)) {
    throw new Error(`${from} → ${to} 로는 바꿀 수 없습니다.`);
  }
  if (isDevPreview()) return;

  const client = sb();
  const { error } = await client
    .from('cad_version')
    .update({ status: to })
    .eq('id', versionId)
    // 낙관적 동시성 — 그 사이 남이 상태를 바꿨으면 0행이 갱신되고 조용히 덮어쓰지 않는다
    .eq('status', from);
  fail('상태 변경', error);

  await logActivity('version.status', { version: versionId, payload: { from, to } });
}

/* -------------------------------------------------------------------------- *
 * 연산 로그 (P4-10 타임라인)
 * -------------------------------------------------------------------------- */

export interface OperationRow {
  id: string;
  seq: number;
  kind: CadOp['kind'];
  command: string;
  entity_ids: string[];
  before: unknown;
  after: unknown;
  actor: string;
  at: string;
}

/** 버전의 연산 로그를 순서대로 가져온다 */
export async function listOperations(versionId: string): Promise<OperationRow[]> {
  if (isDevPreview()) return [];

  const client = sb();
  const { data, error } = await client
    .from('cad_operation')
    .select('id, seq, kind, command, entity_ids, before, after, actor, at')
    .eq('version_id', versionId)
    .order('seq', { ascending: true });
  fail('연산 로그 조회', error);

  return (data ?? []) as OperationRow[];
}

/**
 * DB 행을 편집기 연산으로 되돌린다.
 *
 * 저장할 때 `before`/`after` 를 그대로 넣었으므로 형태가 맞는다.
 * 형태가 안 맞는 행은 **건너뛴다** — 옛 스키마로 저장된 로그 하나 때문에
 * 타임라인 전체가 죽으면 안 된다.
 */
export function rowToOp(row: OperationRow): CadOp | null {
  const common = { command: row.command };
  switch (row.kind) {
    case 'add':
    case 'delete':
      return Array.isArray(row.after)
        ? ({ kind: row.kind, ...common, entities: row.after } as CadOp)
        : null;
    case 'update':
      return Array.isArray(row.before) && Array.isArray(row.after)
        ? ({ kind: 'update', ...common, before: row.before, after: row.after } as CadOp)
        : null;
    case 'layer':
      return Array.isArray(row.before) && Array.isArray(row.after)
        ? ({ kind: 'layer', ...common, before: row.before, after: row.after } as CadOp)
        : null;
    case 'meta':
      return row.before && row.after
        ? ({ kind: 'meta', ...common, before: row.before, after: row.after } as CadOp)
        : null;
    default:
      return null;
  }
}

/**
 * 연산 로그를 명령 단위 묶음으로 되돌린다.
 *
 * 같은 명령이 만든 연산들은 한 덩어리로 취급해야 타임라인 스크럽이
 * "MOVE 한 번" 단위로 움직인다. 연산 하나씩 움직이면 사용자는
 * 폴리선 정점이 하나씩 튀는 걸 보게 된다.
 */
export function groupOperations(rows: OperationRow[]): TimelineStep[] {
  const steps: TimelineStep[] = [];
  let current: TimelineStep | null = null;

  for (const row of rows) {
    const op = rowToOp(row);
    if (!op) continue;

    const at = new Date(row.at).getTime();

    // 같은 명령 + 같은 사람 + 1초 이내면 한 덩어리로 본다
    const sameGroup =
      current !== null &&
      current.batch.command === row.command &&
      current.actor === row.actor &&
      Math.abs(at - current.batch.at) < 1000;

    if (!sameGroup) {
      current = {
        batch: { id: row.id, command: row.command, ops: [], at },
        actor: row.actor,
        seq: row.seq,
      };
      steps.push(current);
    }
    current!.batch.ops.push(op);
  }

  return steps;
}

/** 타임라인 한 칸 — 명령 하나에 해당한다 */
export interface TimelineStep {
  batch: OpBatch;
  actor: string;
  /** 이 덩어리의 첫 연산 순번 */
  seq: number;
}

/* -------------------------------------------------------------------------- *
 * 개발 미리보기
 * -------------------------------------------------------------------------- */

const DEV_VERSIONS = new Map<string, VersionListItem>();
const DEV_BRANCHES = new Map<string, BranchRow>();
const DEV_DOCS = new Map<string, CadDocument>();

/** 미리보기 모드에서 편집기가 연 도면 본문을 등록한다 (비교 화면이 읽는다) */
export function registerDevDocument(versionId: string, doc: CadDocument): void {
  if (!isDevPreview()) return;
  DEV_DOCS.set(versionId, doc);
}

function devNow(offsetMin: number): string {
  return new Date(Date.now() - offsetMin * 60_000).toISOString();
}

function devVersions(drawingId: string): VersionListItem[] {
  if (DEV_VERSIONS.size === 0) {
    const seed: VersionListItem[] = [
      {
        id: 'v-dev',
        drawing_id: drawingId,
        branch_id: 'b-dev',
        parent_version_id: 'v-dev-0',
        version_number: 2,
        name: 'v2',
        change_summary: '신선 코너 확장',
        tags: [],
        status: 'draft',
        entity_count: 16,
        created_by: 'dev@localhost',
        created_at: devNow(10),
      },
      {
        id: 'v-dev-0',
        drawing_id: drawingId,
        branch_id: 'b-dev',
        parent_version_id: null,
        version_number: 1,
        name: 'v1',
        change_summary: '최초 작성',
        tags: [],
        status: 'approved',
        entity_count: 15,
        created_by: 'dev@localhost',
        created_at: devNow(180),
      },
    ];
    for (const v of seed) DEV_VERSIONS.set(v.id, v);
  }
  return [...DEV_VERSIONS.values()]
    .filter((v) => v.drawing_id === drawingId)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
}

function devBranches(drawingId: string): BranchRow[] {
  if (DEV_BRANCHES.size === 0) {
    DEV_BRANCHES.set('b-dev', {
      id: 'b-dev',
      drawing_id: drawingId,
      name: 'main',
      parent_version_id: null,
      is_main: true,
      created_by: 'dev@localhost',
      created_at: devNow(180),
    });
  }
  return [...DEV_BRANCHES.values()].filter((b) => b.drawing_id === drawingId);
}

function devDocument(versionId: string): CadDocument {
  const hit = DEV_DOCS.get(versionId);
  if (hit) return hit;

  const current = [...DEV_DOCS.values()][0];
  if (!current) {
    throw new Error('미리보기 모드에서는 편집기를 먼저 열어야 비교할 수 있습니다.');
  }

  // 씨앗 버전(v-dev-0)은 "리뉴얼 이전" 을 흉내 낸다.
  // 같은 본문을 두 번 주면 비교 화면이 항상 "변화 없음" 이라 화면을 검증할 수 없다.
  // ⚠️ 미리보기 전용이다. 실데이터 경로는 위쪽 DB 조회를 탄다.
  if (versionId === 'v-dev-0') {
    const before = devPreviousDocument(current);
    DEV_DOCS.set(versionId, before);
    return before;
  }

  return current;
}

/** 현재 도면에서 "이전 안" 을 만들어 낸다 — 하나 지우고, 하나 옮기고, 하나 늘린다 */
function devPreviousDocument(doc: CadDocument): CadDocument {
  const fixtures = doc.entities.filter((e) => e.layer === 'F-FIXTURE');
  const dropId = fixtures.at(-1)?.id;
  const moveId = fixtures[0]?.id;

  return {
    ...doc,
    entities: doc.entities
      .filter((e) => e.id !== dropId)
      .map((e) => {
        if (e.id !== moveId) return e;
        // 2m 옆에 있던 것을 옮긴 것으로 본다 → '이설' 로 잡혀야 한다
        if (e.type === 'blockref') return { ...e, at: { x: e.at.x - 2_000_000, y: e.at.y } };
        if (e.type === 'polyline') {
          return { ...e, points: e.points.map((p) => ({ x: p.x - 2_000_000, y: p.y })) };
        }
        return e;
      }),
  };
}

function devNewBranch(drawingId: string, fromVersionId: string, name: string): BranchRow {
  const branch: BranchRow = {
    id: `b-${Date.now().toString(36)}`,
    drawing_id: drawingId,
    name,
    parent_version_id: fromVersionId,
    is_main: false,
    created_by: 'dev@localhost',
    created_at: new Date().toISOString(),
  };
  DEV_BRANCHES.set(branch.id, branch);
  return branch;
}

function devNewVersion(input: CreateVersionInput, doc: CadDocument): DrawingVersion {
  const inBranch = [...DEV_VERSIONS.values()].filter((v) => v.branch_id === input.branchId);
  const next = inBranch.reduce((m, v) => Math.max(m, v.version_number), 0) + 1;

  const row: VersionListItem = {
    id: `v-${Date.now().toString(36)}`,
    drawing_id: input.drawingId,
    branch_id: input.branchId,
    parent_version_id: input.parentVersionId ?? null,
    version_number: next,
    name: input.name ?? `v${next}`,
    change_summary: input.changeSummary ?? null,
    tags: [],
    status: 'draft',
    entity_count: doc.entities.length,
    created_by: 'dev@localhost',
    created_at: new Date().toISOString(),
  };
  DEV_VERSIONS.set(row.id, row);
  DEV_DOCS.set(row.id, doc);
  return row;
}
